"use client"

import type { AuthMethod } from "@defuse-protocol/internal-utils"
import { authIdentity } from "@defuse-protocol/internal-utils"
import { createPrivateBalanceQueryOptions } from "@src/components/DefuseSDK/features/account/queries/privateBalanceQueries"
import { buildPrivateBalanceMap } from "@src/components/DefuseSDK/features/account/utils/privateHoldingsMapper"
import { useTokensUsdPrices } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import { createDepositedBalanceQueryOptions } from "@src/components/DefuseSDK/queries/balanceQueries"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import { getTokenId } from "@src/components/DefuseSDK/utils/token"
import {
  computeTotalBalanceDifferentDecimals,
  getUnderlyingBaseTokenInfos,
} from "@src/components/DefuseSDK/utils/tokenUtils"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import {
  resolveVaultSharePriceUsd,
  resolveVaultSharePriceUsdc,
} from "./earnVaultPricing"
import type { VaultConfig } from "./types"

export type VaultDepositedBalance = {
  amountLabel: string
  usdcPrice: number | null
  usdcValue: number | null
  usdPrice: number | null
  usdValue: number | null
  hasBalance: boolean
}

export type VaultDepositedBalances = {
  balances: Map<string, VaultDepositedBalance>
  /** True while balances or USD prices await their first response. */
  isLoading: boolean
}

export function useVaultDepositedBalances(
  vaults: VaultConfig[],
  userAddress: string | null | undefined,
  userChainType: AuthMethod | null | undefined,
  options: { isConfidential: boolean }
): VaultDepositedBalances {
  const userId =
    userAddress != null && userChainType != null
      ? authIdentity.authHandleToIntentsUserId(userAddress, userChainType)
      : null
  const { isConfidential } = options

  const tokenIds = useMemo(
    () =>
      vaults
        .flatMap((vault) => getUnderlyingBaseTokenInfos(vault.vaultToken))
        .map(getTokenId),
    [vaults]
  )

  const publicBalanceQuery = useQuery(
    createDepositedBalanceQueryOptions(
      { userId, tokenIds },
      !isConfidential && userId != null && tokenIds.length > 0
    )
  )
  const privateBalanceQuery = useQuery({
    ...createPrivateBalanceQueryOptions({ userId }),
    enabled: isConfidential && userId != null,
  })
  const { data: tokensUsdPriceData, isPending: tokenPricesPending } =
    useTokensUsdPrices()

  const balancePending = isConfidential
    ? privateBalanceQuery.data == null
    : publicBalanceQuery.data == null
  const isLoading = balancePending || tokenPricesPending

  const balances = useMemo(() => {
    const balanceMapping = isConfidential
      ? buildPrivateBalanceMap(privateBalanceQuery.data)
      : (publicBalanceQuery.data ?? {})
    const depositedByVaultId = new Map<string, VaultDepositedBalance>()

    for (const vault of vaults) {
      const value = computeTotalBalanceDifferentDecimals(
        vault.vaultToken,
        balanceMapping,
        { strict: false }
      )

      const sharePriceUsd = resolveVaultSharePriceUsd({
        vaultToken: vault.vaultToken,
        tokensUsdPriceData,
      })
      const sharePriceUsdc = resolveVaultSharePriceUsdc({
        vaultToken: vault.vaultToken,
        paymentToken: vault.paymentToken,
        tokensUsdPriceData,
      })

      if (value == null || value.amount === 0n) {
        depositedByVaultId.set(vault.vaultToken.defuseAssetId, {
          amountLabel: "0",
          usdcPrice: sharePriceUsdc,
          usdcValue: sharePriceUsdc == null ? null : 0,
          usdPrice: sharePriceUsd,
          usdValue: 0,
          hasBalance: false,
        })
        continue
      }

      const displayAmount = formatTokenValue(value.amount, value.decimals, {
        fractionDigits: 4,
      })
      const shareAmount = Number(formatTokenValue(value.amount, value.decimals))
      const usdValue =
        sharePriceUsd == null || !Number.isFinite(shareAmount * sharePriceUsd)
          ? null
          : shareAmount * sharePriceUsd
      const usdcValue =
        sharePriceUsdc == null ||
        !Number.isFinite(shareAmount * sharePriceUsdc)
          ? null
          : shareAmount * sharePriceUsdc

      depositedByVaultId.set(vault.vaultToken.defuseAssetId, {
        amountLabel: `${displayAmount} ${vault.vaultToken.symbol}`,
        usdcPrice: sharePriceUsdc,
        usdcValue,
        usdPrice: sharePriceUsd,
        usdValue,
        hasBalance: true,
      })
    }

    return depositedByVaultId
  }, [
    vaults,
    isConfidential,
    publicBalanceQuery.data,
    privateBalanceQuery.data,
    tokensUsdPriceData,
  ])

  return { balances, isLoading }
}
